import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import LanguageSwitcher from '../../components/base/LanguageSwitcher';
import { authService, isClientDepartmentUser } from '../../services/auth.service';
import { clearTokens } from '../../services/api';
import { setPortalToken } from '../../services/portal.service';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login, loading, logout, clearStaffUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPwd, setShowForgotPwd] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  useEffect(() => {
    const savedEmail = localStorage.getItem('cysol_remember_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const emailNorm = email.trim().toLowerCase();
    try {
      const me = await login(emailNorm, password);
      if (rememberMe) {
        localStorage.setItem('cysol_remember_email', emailNorm);
      } else {
        localStorage.removeItem('cysol_remember_email');
      }
      if (isClientDepartmentUser(me)) {
        try {
          const portal = await authService.exchangePortalToken();
          clearTokens();
          setPortalToken(portal.access_token);
          clearStaffUser();
          navigate('/submit-ticket?view=portal', { replace: true });
          return;
        } catch {
          sessionStorage.setItem('cysol_portal_notice', t('login.clientPortalLinkMissing'));
          await logout();
          navigate('/submit-ticket?view=portal', { replace: true });
          return;
        }
      }
      navigate(from, { replace: true });
    } catch {
      setError(t('login.errorInvalidCredentials'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <img 
              src="https://www.cysol.com.ar/assets/grupo-cysol-logo-Ci16Najk.png" 
              alt="Cysol Logo" 
              className="h-12 mb-8"
            />
            <h1 className="text-3xl font-extrabold text-neutral-900 mb-2 tracking-tight">{t('login.welcomeBack')}</h1>
            <p className="text-base text-neutral-600">{t('login.subtitle')}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5"></i>
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-neutral-900 mb-2">
                {t('login.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all"
                placeholder={t('login.emailPlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-neutral-900 mb-2">
                {t('login.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all"
                placeholder={t('login.passwordPlaceholder')}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-cysol-red focus:ring-cysol-red border-neutral-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-neutral-700 cursor-pointer font-medium">
                  {t('login.rememberMe')}
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowForgotPwd(!showForgotPwd)}
                className="text-sm font-semibold text-cysol-red hover:text-cysol-red-dark whitespace-nowrap transition-colors"
              >
                {t('login.forgotPassword')}
              </button>
            </div>

            {showForgotPwd && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 mb-2">{t('login.forgotInstructions')}</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder={t('login.forgotEmailPlaceholder')}
                    className="flex-1 px-3 py-1.5 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!forgotEmail) return;
                      try {
                        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/forgot-password`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: forgotEmail }),
                        });
                      } catch {
                        // intentionally ignore
                      }
                      setForgotMsg(t('login.forgotSuccess'));
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {t('login.send')}
                  </button>
                </div>
                {forgotMsg && <p className="text-sm text-green-700 mt-2">{forgotMsg}</p>}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="w-full bg-cysol-red text-white py-3 px-4 rounded-lg text-sm font-semibold hover:brightness-110 transition-all shadow-lg shadow-red-900/30 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('login.signingIn')}
                </>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-neutral-600">
                {t('login.needAccess')}{' '}
                <a href="mailto:info@cysol.com.ar" className="font-semibold text-cysol-red hover:text-cysol-red-dark whitespace-nowrap transition-colors">
                  {t('login.contactAdmin')}
                </a>
              </p>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Branded section */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-reflejo"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <span className="inline-flex items-center gap-2 rounded-full bg-cysol-red px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white shadow-lg shadow-red-900/30 mb-6">
              {t('login.brandBadge')}
            </span>
            <h2 className="text-4xl font-extrabold mb-6 tracking-tight leading-tight">
              {t('login.brandTitle')}
            </h2>
            <p className="text-lg text-white/85 mb-8 leading-relaxed">
              {t('login.brandDescription')}
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cysol-red/15 border border-cysol-red/30">
                  <i className="ri-check-line text-cysol-red text-lg"></i>
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold">{t('login.feature1')}</p>
                  <p className="text-sm text-white/70 mt-1">{t('login.feature1Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cysol-red/15 border border-cysol-red/30">
                  <i className="ri-check-line text-cysol-red text-lg"></i>
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold">{t('login.feature2')}</p>
                  <p className="text-sm text-white/70 mt-1">{t('login.feature2Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cysol-red/15 border border-cysol-red/30">
                  <i className="ri-check-line text-cysol-red text-lg"></i>
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold">{t('login.feature3')}</p>
                  <p className="text-sm text-white/70 mt-1">{t('login.feature3Desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
